/**
 * Customer Authentication API
 * Handles all API calls for customer auth endpoints
 * Uses /api/auth/customer/ routes
 */

import { axiosInstance } from "../lib/axios";
import { API_BASE_URL, getAuthHeaders } from "../config";
import {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  CustomerProfileDto,
  UpdateProfileRequest,
  ChangePasswordRequest,
  ApiErrorResponse,
} from "../types/customer-auth.types";

const CUSTOMER_AUTH_BASE_URL = `${API_BASE_URL}/auth/customer`;

/**
 * Customer Login
 * POST /api/auth/customer/login
 */
export const loginCustomer = async (
  loginData: LoginRequest
): Promise<LoginResponse> => {
  try {
    const response = await axiosInstance.post<any>(
      `${CUSTOMER_AUTH_BASE_URL}/login`,
      loginData
    );
    
    // Backend returns: { success, message, data: { customerId, token, customer } }
    // We need to flatten it to match LoginResponse type
    const apiResponse = response.data;
    
    if (apiResponse.data) {
      // Extract data from nested structure
      return {
        success: apiResponse.success,
        message: apiResponse.message,
        token: apiResponse.data.token,
        customerAuthId: apiResponse.data.customerId, // Backend uses customerId
        phoneNumber: apiResponse.data.customer?.phoneNumber || loginData.phoneNumber,
        restaurantId: apiResponse.data.customer?.restaurantId || loginData.restaurantId,
        role: "customer",
        customerData: apiResponse.data.customer, // Backend uses customer, not customerData
      };
    }
    
    // Fallback for flat structure
    return apiResponse as LoginResponse;
  } catch (error: any) {
    const errorData: ApiErrorResponse = {
      success: false,
      message: error.response?.data?.message || "Login failed",
      statusCode: error.response?.status || 500,
      errors: error.response?.data?.errors,
    };
    throw errorData;
  }
};

/**
 * Customer Registration
 * POST /api/auth/customer/register
 */
export const registerCustomer = async (
  registerData: RegisterRequest
): Promise<RegisterResponse> => {
  try {
    const response = await axiosInstance.post<any>(
      `${CUSTOMER_AUTH_BASE_URL}/register`,
      registerData
    );
    
    // Backend returns: { success, message, data: { customerId, token, customer } }
    // We need to flatten it to match RegisterResponse type
    const apiResponse = response.data;
    
    if (apiResponse.data) {
      // Extract data from nested structure
      return {
        success: apiResponse.success,
        message: apiResponse.message,
        token: apiResponse.data.token,
        customerAuthId: apiResponse.data.customerId, // Backend uses customerId
        phoneNumber: apiResponse.data.customer?.phoneNumber || registerData.phoneNumber,
        restaurantId: apiResponse.data.customer?.restaurantId || registerData.restaurantId,
        role: "customer",
        customerData: apiResponse.data.customer, // Backend uses customer, not customerData
      };
    }
    
    // Fallback for flat structure
    return apiResponse as RegisterResponse;
  } catch (error: any) {
    const errorData: ApiErrorResponse = {
      success: false,
      message: error.response?.data?.message || "Registration failed",
      statusCode: error.response?.status || 500,
      errors: error.response?.data?.errors,
    };
    throw errorData;
  }
};

/**
 * Get Customer Profile
 * GET /api/auth/customer/profile
 * Requires: Bearer token
 */
export const getCustomerProfile = async (
  token: string
): Promise<CustomerProfileDto> => {
  try {
    const response = await axiosInstance.get<any>(
      `${CUSTOMER_AUTH_BASE_URL}/profile`,
      {
        headers: getAuthHeaders(token),
      }
    );
    
    // Backend returns: { success, message, data: { customerId, token, customer: {...} } }
    // Extract the actual profile data from the nested structure
    if (response.data && response.data.data) {
      // Check if customer data is nested inside a 'customer' field
      if (response.data.data.customer) {
        return {
          ...response.data.data.customer,
          id: response.data.data.customerId || response.data.data.customer.id,
        } as CustomerProfileDto;
      }
      
      return response.data.data as CustomerProfileDto;
    }
    
    // Fallback: Check if customer is directly in response.data
    if (response.data && response.data.customer) {
      return {
        ...response.data.customer,
        id: response.data.customerId || response.data.customer.id,
      } as CustomerProfileDto;
    }
    
    // Fallback for flat structure
    return response.data as CustomerProfileDto;
  } catch (error: any) {
    const errorData: ApiErrorResponse = {
      success: false,
      message: error.response?.data?.message || "Failed to fetch profile",
      statusCode: error.response?.status || 500,
      errors: error.response?.data?.errors,
    };
    throw errorData;
  }
};

/**
 * Update Customer Profile
 * PUT /api/auth/customer/profile
 * Requires: Bearer token
 * Uses multipart/form-data for profile picture upload
 */
export const updateCustomerProfile = async (
  token: string,
  updateData: UpdateProfileRequest
): Promise<CustomerProfileDto> => {
  try {
    // Create FormData for multipart/form-data request
    const formData = new FormData();
    
    if (updateData.fullName) {
      formData.append('FullName', updateData.fullName);
    }
    if (updateData.email) {
      formData.append('Email', updateData.email);
    }
    if (updateData.dateOfBirth) {
      formData.append('DateOfBirth', updateData.dateOfBirth);
    }
    if (updateData.phoneNumber) {
      formData.append('PhoneNumber', updateData.phoneNumber);
    }
    if (updateData.profilePicture) {
      formData.append('ProfilePicture', updateData.profilePicture);
    }
    if (updateData.removeProfilePicture) {
      formData.append('RemoveProfilePicture', 'true');
    }

    const response = await axiosInstance.put<any>(
      `${CUSTOMER_AUTH_BASE_URL}/profile`,
      formData,
      {
        headers: {
          ...getAuthHeaders(token),
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    // Backend returns: { success, message, data: { customer, token, ... } }
    // Extract the customer data from the nested response
    if (response.data.data && response.data.data.customer) {
      return response.data.data.customer;
    }
    
    return response.data;
  } catch (error: any) {
    const errorData: ApiErrorResponse = {
      success: false,
      message: error.response?.data?.message || "Failed to update profile",
      statusCode: error.response?.status || 500,
      errors: error.response?.data?.errors,
    };
    throw errorData;
  }
};

/**
 * Change Customer Password
 * POST /api/auth/customer/change-password
 * Requires: Bearer token
 */
export const changeCustomerPassword = async (
  token: string,
  changePasswordData: ChangePasswordRequest
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axiosInstance.post<{
      success: boolean;
      message: string;
    }>(
      `${CUSTOMER_AUTH_BASE_URL}/change-password`,
      changePasswordData,
      {
        headers: getAuthHeaders(token),
      }
    );
    return response.data;
  } catch (error: any) {
    const errorData: ApiErrorResponse = {
      success: false,
      message: error.response?.data?.message || "Failed to change password",
      statusCode: error.response?.status || 500,
      errors: error.response?.data?.errors,
    };
    throw errorData;
  }
};

/**
 * Customer Logout
 * POST /api/auth/customer/logout
 * Requires: Bearer token
 */
export const logoutCustomer = async (token: string): Promise<{
  success: boolean;
  message: string;
}> => {
  try {
    const response = await axiosInstance.post<{
      success: boolean;
      message: string;
    }>(
      `${CUSTOMER_AUTH_BASE_URL}/logout`,
      {},
      {
        headers: getAuthHeaders(token),
      }
    );
    return response.data;
  } catch (error: any) {
    const errorData: ApiErrorResponse = {
      success: false,
      message: error.response?.data?.message || "Logout failed",
      statusCode: error.response?.status || 500,
      errors: error.response?.data?.errors,
    };
    throw errorData;
  }
};

/**
 * Customer Forgot Password
 * POST /api/auth/customer/forgot-password
 */
export const forgotPasswordCustomer = async (
  data: { email: string; restaurantId: number; clientUri: string }
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axiosInstance.post<{
      success: boolean;
      message: string;
    }>(`${CUSTOMER_AUTH_BASE_URL}/forgot-password`, data);
    return response.data;
  } catch (error: any) {
    const errorData: ApiErrorResponse = {
      success: false,
      message: error.response?.data?.message || "Failed to send reset link",
      statusCode: error.response?.status || 500,
      errors: error.response?.data?.errors,
    };
    throw errorData;
  }
};

/**
 * Customer Reset Password
 * POST /api/auth/customer/reset-password
 */
export const resetPasswordCustomer = async (
  data: { email: string; restaurantId: number; token: string; newPassword: string }
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axiosInstance.post<{
      success: boolean;
      message: string;
    }>(`${CUSTOMER_AUTH_BASE_URL}/reset-password`, data);
    return response.data;
  } catch (error: any) {
    const errorData: ApiErrorResponse = {
      success: false,
      message: error.response?.data?.message || "Failed to reset password",
      statusCode: error.response?.status || 500,
      errors: error.response?.data?.errors,
    };
    throw errorData;
  }
};
